import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigProvider, DatePicker, Input, Modal, Select } from 'antd';
import plPL from 'antd/locale/pl_PL';
import ruRU from 'antd/locale/ru_RU';
// antd 5 работает на dayjs, а не на moment: moment в зависимостях нет вовсе.
import dayjs, { Dayjs } from 'dayjs';
// Регистрация локалей dayjs — БЕЗ `dayjs.locale(...)`: глобальная локаль
// поменяла бы форматирование дат во всём LMS. Пикер берёт язык из
// `ConfigProvider locale` только внутри этой формы.
import 'dayjs/locale/pl';
import 'dayjs/locale/ru';

import styled from 'styled-components';

import { CareerIcon } from 'atoms';
import { Colors } from 'context';
import {
  APPLICATION_STATUSES,
  CareerApplicationStatus,
  ICareerApplication,
  ICareerApplicationInput,
} from 'core';
import { ApplicationScenario } from 'router/careerLinks';
import { careerColor, careerTokenVars, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

interface ICareerApplicationForm {
  open: boolean;
  /** `null` — заводим новый отклик. */
  application: ICareerApplication | null;
  /** С чего начинается НОВАЯ запись: «уже отправил» или «планирую». */
  scenario: ApplicationScenario;
  saving: boolean;
  onSubmit: (input: ICareerApplicationInput) => void;
  onClose: () => void;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const valuesFor = (scenario: ApplicationScenario) => ({
  companyName: '',
  position: '',
  applyUrl: '',
  status: (scenario === 'sent' ? 'applied' : 'planned') as CareerApplicationStatus,
  // Сегодня — по ЛОКАЛЬНЫМ часам студента: вечером в Варшаве UTC-дата уже завтрашняя.
  appliedAt: scenario === 'sent' ? dayjs() : null,
  nextActionAt: null as Dayjs | null,
  note: '',
});

type Values = ReturnType<typeof valuesFor>;

/**
 * Форма отклика: завести или поправить (0072, шаг 1).
 *
 * Аудит в Brave: «Zapisz aplikację wysłaną poza LearnIT» открывала форму со
 * статусом «Planuję» — человек записывал отправленный отклик как план, и
 * воронка его не видела. Теперь сценарий приходит явно: «уже отправил» —
 * «Wysłana» и сегодняшняя дата (её можно поправить), «планирую» — «Planuję».
 *
 * Обязательны компания и должность: запись «Acme — ?» в журнале не читается,
 * а напоминание о контакте не говорит, о каком отклике речь. Ссылка — нет.
 *
 * 🔴 Ошибка сохранения форму не закрывает и введённое не стирает — закрывает
 * её только родитель после успеха.
 */
export const CareerApplicationForm = memo(
  ({ open, application, scenario, saving, onSubmit, onClose }: ICareerApplicationForm) => {
    const { t, i18n } = useTranslation();
    const [values, setValues] = useState<Values>(valuesFor(scenario));
    const [touched, setTouched] = useState(false);

    // Значения подставляются при КАЖДОМ открытии, а не один раз при монтаже:
    // модалка живёт в дереве постоянно, и без этого правка второго отклика
    // подряд показала бы поля первого.
    useEffect(() => {
      if (!open) {
        return;
      }
      setTouched(false);
      setValues(
        application
          ? {
              companyName: application.companyName,
              position: application.position,
              applyUrl: application.applyUrl,
              status: application.status,
              appliedAt: application.appliedAt ? dayjs(application.appliedAt, DATE_FORMAT) : null,
              nextActionAt: application.nextActionAt
                ? dayjs(application.nextActionAt, DATE_FORMAT)
                : null,
              note: application.note,
            }
          : valuesFor(scenario),
      );
    }, [open, application, scenario]);

    const companyMissing = !values.companyName.trim();
    // У старой записи без должности (импорт самоотметок) требовать её при
    // правке не будем — сервер так же.
    const positionMissing = !values.position.trim() && !(application && !application.position);

    const set = (patch: Partial<Values>) => setValues((prev) => ({ ...prev, ...patch }));

    const submit = () => {
      setTouched(true);
      if (companyMissing || positionMissing) {
        return;
      }
      onSubmit({
        companyName: values.companyName.trim(),
        position: values.position.trim(),
        applyUrl: values.applyUrl.trim(),
        status: values.status,
        appliedAt: values.appliedAt ? values.appliedAt.format(DATE_FORMAT) : null,
        nextActionAt: values.nextActionAt ? values.nextActionAt.format(DATE_FORMAT) : null,
        note: values.note,
      });
    };

    const title = application
      ? t('careerCenter.applications.edit')
      : scenario === 'sent'
      ? t('careerCenter.applications.addSent')
      : t('careerCenter.applications.addPlanned');

    return (
      <ConfigProvider locale={i18n.language === 'ru' ? ruRU : plPL}>
        <Modal
          open={open}
          // Свой крестик: у antd 5.4 кнопка закрытия подписана «Close» по-английски
          // при любой локали.
          closable={false}
          title={
            <TitleRow>
              <span>{title}</span>
              <CloseButton
                type="button"
                onClick={onClose}
                aria-label={t('careerCenter.applications.close')}>
                <CareerIcon name="close" size={20} />
              </CloseButton>
            </TitleRow>
          }
          onCancel={onClose}
          destroyOnClose={false}
          footer={
            <Footer>
              <SecondaryButton type="button" onClick={onClose}>
                {t('careerCenter.applications.cancel')}
              </SecondaryButton>
              <PrimaryButton type="button" onClick={submit} disabled={saving} aria-busy={saving}>
                {t('careerCenter.applications.save')}
              </PrimaryButton>
            </Footer>
          }>
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}>
            <Field>
              <Label htmlFor="application-company">
                {t('careerCenter.applications.company')}
                <Required aria-hidden> *</Required>
              </Label>
              <Input
                id="application-company"
                value={values.companyName}
                aria-required
                aria-invalid={touched && companyMissing}
                aria-describedby={
                  touched && companyMissing ? 'application-company-error' : undefined
                }
                status={touched && companyMissing ? 'error' : undefined}
                onChange={(event) => set({ companyName: event.target.value })}
              />
              {touched && companyMissing ? (
                <Error id="application-company-error">
                  {t('careerCenter.applications.errors.companyRequired')}
                </Error>
              ) : null}
            </Field>

            <Field>
              <Label htmlFor="application-position">
                {t('careerCenter.applications.position')}
                <Required aria-hidden> *</Required>
              </Label>
              <Input
                id="application-position"
                value={values.position}
                aria-required
                aria-invalid={touched && positionMissing}
                aria-describedby={
                  touched && positionMissing ? 'application-position-error' : undefined
                }
                status={touched && positionMissing ? 'error' : undefined}
                onChange={(event) => set({ position: event.target.value })}
              />
              {touched && positionMissing ? (
                <Error id="application-position-error">
                  {t('careerCenter.applications.errors.positionRequired')}
                </Error>
              ) : null}
            </Field>

            <Field>
              <Label htmlFor="application-url">
                {t('careerCenter.applications.link')}
                <Optional> {t('careerCenter.applications.optional')}</Optional>
              </Label>
              <Input
                id="application-url"
                type="url"
                inputMode="url"
                value={values.applyUrl}
                onChange={(event) => set({ applyUrl: event.target.value })}
              />
            </Field>

            <Row>
              <Field>
                <Label htmlFor="application-status">
                  {t('careerCenter.applications.statusLabel')}
                </Label>
                <Select
                  id="application-status"
                  value={values.status}
                  style={{ width: '100%' }}
                  onChange={(value) => set({ status: value as CareerApplicationStatus })}
                  options={APPLICATION_STATUSES.map((code) => ({
                    value: code,
                    label: t(`careerCenter.applications.status.${code}`),
                  }))}
                />
              </Field>

              <Field>
                <Label htmlFor="application-applied-at">
                  {t('careerCenter.applications.appliedAt')}
                </Label>
                <DatePicker
                  id="application-applied-at"
                  clearIcon={
                    <CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />
                  }
                  value={values.appliedAt}
                  format="DD.MM.YYYY"
                  placeholder={t('careerCenter.applications.datePlaceholder')}
                  style={{ width: '100%' }}
                  onChange={(value) => set({ appliedAt: value })}
                />
              </Field>
            </Row>
            {values.status === 'hired' ? (
              <Hint>{t('careerCenter.applications.hiredHint')}</Hint>
            ) : null}

            <Field>
              <Label htmlFor="application-next-contact">
                {t('careerCenter.applications.followUp')}
                <Optional> {t('careerCenter.applications.optional')}</Optional>
              </Label>
              <DatePicker
                id="application-next-contact"
                clearIcon={
                  <CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />
                }
                value={values.nextActionAt}
                format="DD.MM.YYYY"
                placeholder={t('careerCenter.applications.datePlaceholder')}
                style={{ width: '100%' }}
                onChange={(value) => set({ nextActionAt: value })}
              />
              <Hint>{t('careerCenter.applications.followUpHint')}</Hint>
            </Field>

            <Field>
              <Label htmlFor="application-note">{t('careerCenter.applications.note')}</Label>
              <Input.TextArea
                id="application-note"
                rows={3}
                value={values.note}
                onChange={(event) => set({ note: event.target.value })}
              />
            </Field>
          </Form>
        </Modal>
      </ConfigProvider>
    );
  },
);

const Form = styled.form`
  /* Модалка живёт в портале, вне [data-career-section]: переменные цвета
     объявляются здесь, иначе в тёмной теме текст был бы тёмным на тёмном. */
  ${careerTokenVars}
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;

  /* Поля по шкале раздела: 48 высотой, текст 16. */
  .ant-input,
  .ant-picker,
  .ant-select-single .ant-select-selector {
    min-height: ${careerLayout.primaryButton}px;
    font-size: 16px;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    line-height: 46px;
    height: 46px;
  }
`;

const TitleRow = styled.div`
  ${careerTokenVars}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: ${careerColor.text};
  ${careerType.h3()};
`;

const CloseButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${careerLayout.hitTarget}px;
  height: ${careerLayout.hitTarget}px;
  margin: -10px -12px -10px 0;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text2};
  cursor: pointer;

  &:hover {
    background: ${careerColor.hover};
    color: ${careerColor.text};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Label = styled.label`
  color: ${careerColor.text};
  ${careerType.bodyStrong()};
`;

const Required = styled.span`
  color: ${Colors.signal_red};
`;

const Optional = styled.span`
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Hint = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Error = styled.p`
  margin: 0;
  color: #c1121f;
  ${careerType.meta()};
`;

const Footer = styled.div`
  ${careerTokenVars}
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 24px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${Colors.main};
  color: ${CTA_TEXT};
  cursor: pointer;
  ${careerType.bodyStrong()};

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
`;

const SecondaryButton = styled.button`
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 20px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.bodyStrong()};
`;
